/* eslint-disable lingui/no-unlocalized-strings */
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, LayoutDashboard, Menu, Sparkles, User, X } from "lucide-react";
import { type MouseEvent, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { track } from "@/client/analytics/mixpanel";
import { TheResumeLogo } from "@/client/components/theResumeLogo";
import { useAuthStore } from "@/client/stores/auth";

import { getSegment, INSTITUTIONS_PATH } from "../../institutions/content";
import { ProductHuntBanner } from "./product-hunt-banner";

type ProductNavItem = {
  label: string;
  href: string;
  description: string;
};

const productNavigation: ProductNavItem[] = [
  {
    label: "Spotlight Intros",
    href: "#spotlight-intros",
    description: "Add an AI-managed intro video alongside your resume.",
  },
  {
    label: "Practice Interview",
    href: "/practice-interview",
    description: "Practice out loud with a realtime AI interviewer.",
  },
  {
    label: "AI Job Matching",
    href: "#job-matching",
    description: "Find relevant roles and tailor your preparation.",
  },
  {
    label: "Application Tracker",
    href: "#application-tracker",
    description: "Keep applications and next steps in one workspace.",
  },
  {
    label: "For Educational Institutes",
    href: INSTITUTIONS_PATH,
    description: "Career preparation pilots for cohorts and faculties.",
  },
];

// The candidate story (Spotlight Intros, job matching, tracker sections) lives here; "/" is the hub.
const JOB_SEEKERS_PATH = "/job-seekers";

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProductOpen, setIsProductOpen] = useState(false);
  const [atTop, setAtTop] = useState(true);
  const { pathname, hash } = useLocation();
  const isLoggedIn = useAuthStore((state) => Boolean(state.user));
  const onDark = pathname.startsWith("/showcase-v2");

  const trackInstitutions = () =>
    track("institutions_cta_click", {
      location: "header",
      action: "navigation",
      segment: getSegment(window.location.search),
    });

  useEffect(() => {
    const handleScroll = () => setAtTop(window.scrollY < 20);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!hash) return;
    window.requestAnimationFrame(() => {
      document.getElementById(hash.slice(1))?.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
        block: "start",
      });
    });
  }, [hash, pathname]);

  useEffect(() => {
    if (!isMenuOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsMenuOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [isMenuOpen]);

  const handleProductLink = (event: MouseEvent<HTMLAnchorElement>, item: ProductNavItem) => {
    setIsMenuOpen(false);
    setIsProductOpen(false);

    if (item.href === INSTITUTIONS_PATH) {
      trackInstitutions();
    }

    if (!item.href.startsWith("#") || pathname !== JOB_SEEKERS_PATH) return;

    event.preventDefault();
    window.history.replaceState(null, "", item.href);
    document.getElementById(item.href.slice(1))?.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      block: "start",
    });
  };

  const getNavHref = (href: string) => (href.startsWith("#") ? `${JOB_SEEKERS_PATH}${href}` : href);

  const surfaceClass = onDark
    ? "border-white/10 bg-[#10192e]/82 text-white"
    : atTop
      ? "border-transparent bg-transparent text-[#17233f]"
      : "border-[#17233f]/10 bg-[#f7f3e9]/90 text-[#17233f] shadow-[0_8px_30px_rgba(23,35,63,0.07)]";

  const navLinkClass = `text-xs font-semibold transition ${
    onDark ? "text-white/65 hover:text-white" : "text-[#5d6679] hover:text-[#17233f]"
  }`;

  return (
    <>
      <ProductHuntBanner />

      <header
        className={`fixed inset-x-0 top-0 z-50 border-b backdrop-blur-xl transition-all duration-300 ${surfaceClass}`}
      >
        {!onDark && (
          <div className="border-b border-[#17233f]/10 bg-[#fffaf0]/95 text-[#536078]">
            <div className="mx-auto flex max-w-[1480px] items-center justify-center gap-1.5 px-5 py-1.5 text-[11px] sm:px-8 lg:px-12 xl:px-20">
              <span>Are you a recruiter?</span>
              <a
                href="https://recruit.theresume.io"
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold text-[#245be3] transition hover:text-[#194ac4]"
              >
                Visit theRecruit
              </a>
            </div>
          </div>
        )}

        <div className="mx-auto flex h-[76px] max-w-[1480px] items-center justify-between px-5 sm:px-8 lg:px-12 xl:px-20">
          <Link to="/" aria-label="theResume home" className="relative z-20 shrink-0">
            <TheResumeLogo size={138} />
          </Link>

          <nav className="hidden items-center gap-5 xl:flex" aria-label="Primary navigation">
            <div
              className="relative"
              onMouseEnter={() => setIsProductOpen(true)}
              onMouseLeave={() => setIsProductOpen(false)}
            >
              <button
                type="button"
                className={`flex items-center gap-1 ${navLinkClass}`}
                aria-expanded={isProductOpen}
                aria-haspopup="menu"
                onClick={() => setIsProductOpen(true)}
              >
                Product
                <ChevronDown
                  className={`size-3 transition-transform ${isProductOpen ? "rotate-180" : ""}`}
                />
              </button>

              <AnimatePresence>
                {isProductOpen && (
                  <motion.div
                    className="absolute left-1/2 top-full w-80 -translate-x-1/2 pt-4"
                    initial={{ opacity: 0, y: 8, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.98 }}
                    transition={{ duration: 0.16 }}
                  >
                    <div
                      className="overflow-hidden rounded-xl border border-[#17233f]/10 bg-[#fffaf0]/95 p-2 shadow-[0_20px_60px_rgba(23,35,63,0.14)] backdrop-blur-xl"
                      role="menu"
                    >
                      {productNavigation.map((item) => (
                        <a
                          key={item.href}
                          href={getNavHref(item.href)}
                          onClick={(event) => handleProductLink(event, item)}
                          className="group block rounded-lg p-3 transition hover:bg-white"
                          role="menuitem"
                        >
                          <span className="block text-sm font-bold text-[#17233f] transition group-hover:text-[#245be3]">
                            {item.label}
                          </span>
                          <span className="mt-1 block text-xs leading-relaxed text-[#6b7280]">
                            {item.description}
                          </span>
                        </a>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <Link to="/blog" className={navLinkClass}>
              Blog
            </Link>
            <Link to="/contact" className={navLinkClass}>
              Contact
            </Link>
          </nav>

          <div className="hidden items-center gap-3 xl:flex">
            {isLoggedIn ? (
              <div className="flex items-center gap-3">
                <Link
                  to="/dashboard"
                  className={`inline-flex min-h-10 items-center gap-2 rounded-full border px-5 text-xs font-bold transition ${onDark ? "border-white/20 text-white hover:bg-white/10" : "border-[#17233f]/15 text-[#17233f] hover:bg-white/60"}`}
                >
                  <LayoutDashboard className="size-3.5" />
                  Go to Dashboard
                </Link>
                <div
                  className={`flex size-9 items-center justify-center rounded-full border ${
                    onDark
                      ? "border-white/20 bg-white/10 text-white/70"
                      : "border-[#17233f]/15 bg-white/55 text-[#536078]"
                  }`}
                  aria-hidden="true"
                >
                  <User className="size-4" />
                </div>
              </div>
            ) : (
              <>
                <Link
                  to="/auth/login"
                  className={`px-3 py-2 text-xs font-bold transition ${onDark ? "text-white/70 hover:text-white" : "text-[#536078] hover:text-[#17233f]"}`}
                >
                  Log in
                </Link>
                <Link
                  to="/auth/register"
                  className="group inline-flex min-h-10 items-center gap-2 rounded-md bg-[#245be3] px-5 text-xs font-bold text-white transition hover:-translate-y-0.5 hover:bg-[#194ac4]"
                >
                  Sign up free
                  <Sparkles className="size-3.5 transition-transform group-hover:scale-110" />
                </Link>
              </>
            )}
          </div>

          <button
            type="button"
            className={`relative z-20 flex size-10 items-center justify-center rounded-full border xl:hidden ${onDark ? "border-white/15 text-white" : "border-[#17233f]/15 text-[#17233f]"}`}
            onClick={() => setIsMenuOpen((open) => !open)}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-navigation"
            aria-label={isMenuOpen ? "Close navigation" : "Open navigation"}
          >
            {isMenuOpen ? <X className="size-4" /> : <Menu className="size-4" />}
          </button>
        </div>

        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              id="mobile-navigation"
              className="max-h-[calc(100dvh-102px)] overflow-y-auto border-t border-[#17233f]/10 bg-[#f7f3e9] px-6 pb-7 pt-5 text-[#17233f] shadow-xl xl:hidden"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <nav className="grid" aria-label="Mobile navigation">
                <p className="pb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-[#7a6f5e]">
                  Product
                </p>
                {productNavigation.map((item) => (
                  <a
                    key={item.href}
                    href={getNavHref(item.href)}
                    onClick={(event) => handleProductLink(event, item)}
                    aria-current={pathname === item.href ? "page" : undefined}
                    className="border-b border-[#17233f]/10 py-4"
                  >
                    <span className="block text-sm font-semibold">{item.label}</span>
                    <span className="mt-1 block text-xs leading-relaxed text-[#697386]">
                      {item.description}
                    </span>
                  </a>
                ))}
                <Link
                  to="/blog"
                  onClick={() => setIsMenuOpen(false)}
                  className="border-b border-[#17233f]/10 py-4 text-sm font-semibold"
                >
                  Blog
                </Link>
                <Link
                  to="/contact"
                  onClick={() => setIsMenuOpen(false)}
                  className="border-b border-[#17233f]/10 py-4 text-sm font-semibold"
                >
                  Contact
                </Link>
              </nav>
              <div className="mt-6 grid grid-cols-2 gap-3">
                <Link
                  to={isLoggedIn ? "/dashboard" : "/auth/login"}
                  onClick={() => setIsMenuOpen(false)}
                  className="flex min-h-11 items-center justify-center rounded-full border border-[#17233f]/15 text-xs font-bold"
                >
                  {isLoggedIn ? "Dashboard" : "Log in"}
                </Link>
                <Link
                  to={isLoggedIn ? "/dashboard" : "/auth/register"}
                  onClick={() => setIsMenuOpen(false)}
                  className="flex min-h-11 items-center justify-center rounded-md bg-[#245be3] text-xs font-bold text-white"
                >
                  {isLoggedIn ? "Open workspace" : "Sign up free"}
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};
